import {action, makeAutoObservable, observable} from "mobx";
import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {Bindings} from "data/constants/bindings";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {ILeagues, ILeaguesStore} from "data/stores/leagues/leagues.store";
import type {AxiosError} from "axios";
import type {IApiResponse} from "data/services/http";
import type {IModalsStore} from "data/stores/modals/modals.store";
import {ModalType, RequestState} from "data/enums";
import {find, isEqual} from "lodash";
import {extractErrorMessage} from "data/utils";
import {useNavigate} from "react-router-dom";
import type {IUserStore} from "data/stores/user/user.store";
import {TrackAnalytics} from "data/utils/analytic_service";

interface IProps {
	code?: string;
	navigate: ReturnType<typeof useNavigate>;
}

export interface IJoinLeaguesController extends ViewController<IProps> {
	readonly i18n: ILocalizationStore;

	get leagues(): ILeagues;
	get isLoading(): boolean;

	loadMoreMyLeagues: () => void;
	joinLeague: (id: number) => void;
	isJoinInProgress: (id: number) => boolean;
}

@injectable()
export class JoinLeaguesController implements IJoinLeaguesController {
	@observable _requestState = RequestState.PENDING;
	@observable _joiningLeagueId?: number;
	@observable _navigate!: ReturnType<typeof useNavigate>;
	@observable _code: string | undefined = "";

	get leagues() {
		return this._leaguesStore.leaguesForJoin;
	}

	get isLoading() {
		return isEqual(this._requestState, RequestState.PENDING);
	}

	constructor(
		@inject(Bindings.LocalizationStore) readonly i18n: ILocalizationStore,
		@inject(Bindings.LeaguesStore) private _leaguesStore: ILeaguesStore,
		@inject(Bindings.UserStore) private _userStore: IUserStore,
		@inject(Bindings.ModalsStore) private _modalsStore: IModalsStore
	) {
		makeAutoObservable(this);
	}

	loadMoreMyLeagues = () => {
		this._leaguesStore.fetchMoreLeaguesForJoin().then(this.onSuccess).catch(this.onError);
	};

	@action private onSuccess = () => {
		this._requestState = RequestState.SUCCESS;
		this._joiningLeagueId = undefined;
	};

	@action private onError = (error: AxiosError<IApiResponse>) => {
		this._requestState = RequestState.ERROR;
		this._joiningLeagueId = undefined;

		this._modalsStore.showModal(ModalType.ERROR, {
			message: extractErrorMessage(error),
		});
	};

	@action joinLeague = (id: number) => {
		const code = find(this.leagues.leagues, {id})?.code;

		if (!code) return;

		this._requestState = RequestState.PENDING;
		this._joiningLeagueId = id;

		this._leaguesStore
			.joinToLeague({code})
			.then(() => this.redirectToMyLeagueByCode({code}))
			.catch(this.onError);
	};

	isJoinInProgress = (id: number) => {
		return this._joiningLeagueId === id;
	};

	private redirectToMyLeagueByCode = ({
		code,
		replace = false,
	}: {
		code: string;
		replace?: boolean;
	}) => {
		const {leagues} = this._leaguesStore.myLeagues;
		const id = find(leagues, {code})?.id;
		if (id) this._navigate(`/league/${id}/`, {replace});
	};

	private showJoinModal = (code: string | undefined) => {
		if (code) {
			this._modalsStore.showModal(ModalType.LEAGUE_JOIN, {
				message: "",
			});
		}
	};

	private onLoadPageTrack = () => {
		TrackAnalytics.trackOnLoad({
			PageSection: "join",
		});
	};

	@action init({code, navigate}: IProps) {
		this._navigate = navigate;
		this._code = code;

		this.onLoadPageTrack();

		if (this._userStore.isAuthorized) {
			this._leaguesStore.fetchLeaguesForJoin().then(this.onSuccess).catch(this.onError);
			if (code) {
				this._leaguesStore
					.fetchLeagueByCode({code})
					.then(() => this.showJoinModal(code))
					.catch(this.onError);
			}
		} else {
			if (code) {
				localStorage.setItem("join_code", code);
			}
			this._navigate(`/`, {replace: false});
		}
	}
}
